"use client";

import { Card, CardContent, CardHeader, CardTitle } from "../card.js";
import { Button } from "../button.js";
import { AlertCircle, Calendar, RefreshCw, Mail } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../alert.js";

interface CalendarConnectionErrorProps {
  email: string;
  errorMessage?: string;
  onRetry?: () => void;
}

export function CalendarConnectionError({ 
  email, 
  errorMessage,
  onRetry 
}: CalendarConnectionErrorProps) {
  const displayEmail = email.split('@')[0].replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  return (
    <div className="space-y-6">
      <Card className="border-red-200 bg-red-50">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-red-100 rounded-full">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <CardTitle className="text-xl font-bold text-red-900">
            Calendar Not Connected
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unable to Load Availability</AlertTitle>
            <AlertDescription>
              {displayEmail}'s calendar is not properly connected. We cannot fetch their calendar events to show available booking times.
            </AlertDescription>
          </Alert>

          <div className="bg-white rounded-lg p-4 border border-red-200 space-y-3">
            <div className="flex items-start space-x-3">
              <Calendar className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">What This Means</h4>
                <p className="text-sm text-gray-600">
                  To enable public booking, {displayEmail} needs to connect their Google or Microsoft calendar account in the system settings.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Mail className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Contact Information</h4>
                <p className="text-sm text-gray-600">
                  Please contact {displayEmail} at <a href={`mailto:${email}`} className="text-blue-600 hover:underline">{email}</a> to request they connect their calendar.
                </p>
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs text-gray-500 font-mono break-all">
                {errorMessage}
              </p>
            </div>
          )}

          {onRetry && (
            <div className="flex justify-center pt-2">
              <Button
                onClick={onRetry}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Try Again</span>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

























